import { useToast } from 'primevue/usetoast';
import {
  useAssignResultTemplateToProgrammesMutation,
  useSetProgrammesResultTemplateDefaultMutation,
  useAcceptCoursesForProgrammeMutation,
  useRevokeCoursesFromProgrammeMutation,
} from '@/types/vue-apollo-helpers';
import type {
  AssignResultTemplateToProgrammesInput,
  SetProgrammesResultTemplateDefaultMutationVariables,
} from '@/types/operations';

/**
 * Assign one result template to many programmes x many semesters in a single
 * all-or-nothing backend write. Returns the server summary (applied, counts,
 * conflicts). Errors are re-thrown so the caller decides how to present them.
 */
export function useAssignResultTemplateToProgrammes() {
  const { mutate, loading } = useAssignResultTemplateToProgrammesMutation();

  async function assignToProgrammes(input: AssignResultTemplateToProgrammesInput) {
    const result = await mutate({ input });
    return result?.data?.assignResultTemplateToProgrammes ?? null;
  }

  return { assignToProgrammes, loading };
}

/**
 * Set (or clear) the all-semesters default result template for several
 * programmes. The returned programmes refresh the Apollo cache, so pickers
 * showing each programme's default update without a refetch.
 */
export function useSetProgrammesResultTemplateDefault() {
  const { mutate, loading } = useSetProgrammesResultTemplateDefaultMutation();

  async function setDefault(variables: SetProgrammesResultTemplateDefaultMutationVariables) {
    const result = await mutate(variables);
    return result?.data?.setProgrammesResultTemplateDefault ?? [];
  }

  return { setDefault, loading };
}

/**
 * Composable for accepting courses for programme transfer
 */
export function useAcceptCoursesForProgramme() {
  const toast = useToast();
  const isSubmitting = ref(false);

  const { mutate, onDone, onError } = useAcceptCoursesForProgrammeMutation();

  onDone((result) => {
    isSubmitting.value = false;
    const courses = result.data?.acceptCoursesForProgramme || [];
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Successfully transferred ${courses.length} course(s)`,
      life: 3000,
    });
  });

  onError((error) => {
    isSubmitting.value = false;

    // Handle specific error messages
    const message = error.message || '';
    if (message.includes('already registered under the target programme')) {
      toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'One or more courses are already in the target programme',
        life: 5000,
      });
    } else if (message.includes('only available for tertiary')) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'This feature is only available for tertiary institutions',
        life: 5000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: message || 'Failed to transfer courses',
        life: 5000,
      });
    }
  });

  async function acceptCourses(
    targetProgrammeId: string,
    courseRegisteredIds: string[],
  ) {
    isSubmitting.value = true;
    try {
      const result = await mutate({
        target_programme_id: targetProgrammeId,
        course_registered_ids: courseRegisteredIds,
      });
      return result?.data?.acceptCoursesForProgramme || [];
    } catch (e) {
      // Error handled by onError
      return [];
    }
  }

  return {
    acceptCourses,
    isSubmitting,
    onDone,
    onError,
  };
}

/**
 * Composable for revoking course transfers
 */
export function useRevokeCoursesFromProgramme() {
  const toast = useToast();
  const isSubmitting = ref(false);

  const { mutate, onDone, onError } = useRevokeCoursesFromProgrammeMutation();

  onDone((result) => {
    isSubmitting.value = false;
    const courses = result.data?.revokeCoursesFromProgramme || [];
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Successfully revoked ${courses.length} course transfer(s)`,
      life: 3000,
    });
  });

  onError((error) => {
    isSubmitting.value = false;

    const message = error.message || '';
    if (message.includes('not a transferred course')) {
      toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Selected course was not transferred',
        life: 5000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: message || 'Failed to revoke course transfers',
        life: 5000,
      });
    }
  });

  async function revokeCourses(courseRegisteredIds: string[]) {
    isSubmitting.value = true;
    try {
      const result = await mutate({
        course_registered_ids: courseRegisteredIds,
      });
      return result?.data?.revokeCoursesFromProgramme || [];
    } catch (e) {
      // Error handled by onError
      return [];
    }
  }

  return {
    revokeCourses,
    isSubmitting,
    onDone,
    onError,
  };
}
